import { useState, useRef, useCallback } from 'react';
import { Send, Paperclip, Mic, Sparkles } from 'lucide-react';
import { useChatStore } from '../stores/chatStore';
import { cn } from '../utils/api';

export default function ChatInput() {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { sendMessage, isStreaming, currentChat, createChat } = useChatStore();

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!message.trim() || isStreaming) return;

    const content = message.trim();
    setMessage('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Create new chat if none exists
    if (!currentChat) {
      await createChat();
    }

    // Send message
    await sendMessage(content, true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    adjustTextareaHeight();
  };

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="relative">
        <div className="flex items-end gap-2 bg-legion-card border border-legion-border rounded-2xl p-3 focus-within:border-indigo-500 transition-colors">
          {/* Attach button */}
          <button
            type="button"
            className="p-2 hover:bg-white/10 rounded-xl transition-colors text-gray-400 hover:text-white shrink-0"
            title="Attach file"
          >
            <Paperclip className="w-5 h-5" />
          </button>

          {/* Text input */}
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Message DEATH LEGION V1..."
            rows={1}
            className="flex-1 bg-transparent resize-none outline-none text-white placeholder-gray-500 min-h-[44px] max-h-[200px] py-2.5"
            disabled={isStreaming}
          />

          {/* Voice button */}
          <button
            type="button"
            onClick={() => setIsRecording(!isRecording)}
            className={cn(
              "p-2 rounded-xl transition-colors shrink-0",
              isRecording
                ? "bg-red-500/20 text-red-400 animate-pulse"
                : "hover:bg-white/10 text-gray-400 hover:text-white"
            )}
            title="Voice input"
          >
            <Mic className="w-5 h-5" />
          </button>

          {/* Send button */}
          <button
            type="submit"
            disabled={!message.trim() || isStreaming}
            className={cn(
              "p-2 rounded-xl transition-colors shrink-0",
              message.trim() && !isStreaming
                ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                : "bg-gray-700 text-gray-400 cursor-not-allowed"
            )}
          >
            {isStreaming ? (
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-white rounded-full typing-dot" />
                <span className="w-1.5 h-1.5 bg-white rounded-full typing-dot" />
                <span className="w-1.5 h-1.5 bg-white rounded-full typing-dot" />
              </div>
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-center gap-2 mt-2 text-xs text-gray-500">
          <Sparkles className="w-3 h-3" />
          <span>DEATH LEGION V1 may produce unexpected responses</span>
        </div>
      </form>
    </div>
  );
}
